import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useNoIndex from '../hooks/useNoIndex';
import { getChampionshipDetails, updatePreRace, getWidgetSubscriptionStatus, importRaceBySubsession, addScheduledRace, completeScheduledRace } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const SESSION_KEY = (id) => `organizer_auth_${id}`;

function OrganizerChampionshipDetail() {
  useNoIndex();
  const { id, champId } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const [champ, setChamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('completed');
  const [preRaceSession, setPreRaceSession] = useState(null);
  const [preRaceUrl, setPreRaceUrl] = useState('');
  const [preRaceItelemetry, setPreRaceItelemetry] = useState('');
  const [preRaceSaving, setPreRaceSaving] = useState(false);
  const [preRaceUrls, setPreRaceUrls] = useState({});
  const [importInput, setImportInput] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);

  const [schedTrack, setSchedTrack] = useState('');
  const [schedDate, setSchedDate] = useState('');
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedError, setSchedError] = useState(null);
  const [schedSuccess, setSchedSuccess] = useState(null);
  const [completeInputs, setCompleteInputs] = useState({});  // {league_session_id: subsession_id string}
  const [completeLoading, setCompleteLoading] = useState({});
  const [completeError, setCompleteError] = useState({});

  const isManual = Number(champId) < 0;

  const isAuth = sessionStorage.getItem(SESSION_KEY(id)) === 'true';
  const [subscription, setSubscription] = useState(undefined);
  const [copyStates, setCopyStates] = useState({});

  function handleCopy(key, text) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopyStates(s => ({ ...s, [key]: true }));
    setTimeout(() => setCopyStates(s => ({ ...s, [key]: false })), 2000);
  }

  function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  }

  useEffect(() => {
    getChampionshipDetails(champId)
      .then(data => {
        setChamp(data);
        const urls = {};
        (data.sessions || []).forEach(s => {
          if (!s.subsession_id && s.broadcast_url) urls[s.league_session_id] = s.broadcast_url;
        });
        setPreRaceUrls(urls);
      })
      .catch(() => setChamp(null))
      .finally(() => setLoading(false));
    // Load widget subscription for this league
    const pwd = sessionStorage.getItem(`organizer_pwd_${id}`);
    if (pwd) {
      getWidgetSubscriptionStatus(id, pwd)
        .then(data => setSubscription(data.active ? data : null))
        .catch(() => setSubscription(null));
    } else {
      setSubscription(null);
    }
  }, [champId, id]);

  if (!isAuth) {
    return (
      <div className="OrganizerPage">
        <Header />
        <main>
          <div className="organizer-content">
            <p className="organizer-error">{t('organizer.restrictedAccess')} <Link to={`/leagues/${id}/organizer`}>{t('organizer.loginPanel')}</Link></p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-loading">{t('common.loading')}</p></div></main>
      <Footer />
    </div>
  );

  if (!champ) return (
    <div className="OrganizerPage">
      <Header />
      <main><div className="organizer-content"><p className="organizer-error">{t('organizer.champNotFound')}</p></div></main>
      <Footer />
    </div>
  );

  async function handleSavePreRace(leagueSessionId) {
    setPreRaceSaving(true);
    try {
      const pwd = sessionStorage.getItem(`organizer_pwd_${id}`);
      await updatePreRace(leagueSessionId, pwd, preRaceUrl, preRaceItelemetry);
      setPreRaceUrls(prev => ({ ...prev, [leagueSessionId]: preRaceUrl }));
      setPreRaceSession(null);
    } catch {
      alert(t('organizer.saveError'));
    } finally {
      setPreRaceSaving(false);
    }
  }

  async function handleImportRace(e) {
    e.preventDefault();
    setImportError(null);
    setImportSuccess(null);
    const sid = importInput.trim();
    if (!sid) return;
    setImportLoading(true);
    try {
      const pwd = sessionStorage.getItem(`organizer_pwd_${id}`);
      const result = await importRaceBySubsession(champId, sid, pwd);
      setImportSuccess(`${result.track_name}${result.config_name ? ' — ' + result.config_name : ''} (${result.results_count} pilotos)`);
      setImportInput('');
      // Reload championship data
      const data = await getChampionshipDetails(champId);
      setChamp(data);
    } catch (err) {
      setImportError(err?.error || t('organizer.errorSave'));
    } finally {
      setImportLoading(false);
    }
  }

  async function handleCompleteRace(leagueSessionId) {
    const sid = (completeInputs[leagueSessionId] || '').trim();
    if (!sid) return;
    setCompleteLoading(s => ({ ...s, [leagueSessionId]: true }));
    setCompleteError(s => ({ ...s, [leagueSessionId]: null }));
    try {
      const pwd = sessionStorage.getItem(`organizer_pwd_${id}`);
      await completeScheduledRace(leagueSessionId, sid, pwd);
      setCompleteInputs(s => ({ ...s, [leagueSessionId]: '' }));
      const data = await getChampionshipDetails(champId);
      setChamp(data);
    } catch (err) {
      setCompleteError(s => ({ ...s, [leagueSessionId]: err?.error || t('organizer.errorSave') }));
    } finally {
      setCompleteLoading(s => ({ ...s, [leagueSessionId]: false }));
    }
  }

  async function handleAddScheduled(e) {
    e.preventDefault();
    setSchedError(null);
    setSchedSuccess(null);
    if (!schedTrack.trim()) return;
    setSchedLoading(true);
    try {
      const pwd = sessionStorage.getItem(`organizer_pwd_${id}`);
      const result = await addScheduledRace(champId, {
        password: pwd,
        track_name: schedTrack.trim(),
        start_time: schedDate || null,
      });
      const label = result.track_name + (result.config_name ? ` — ${result.config_name}` : '');
      setSchedSuccess(label);
      setSchedTrack(''); setSchedDate('');
      const data = await getChampionshipDetails(champId);
      setChamp(data);
    } catch (err) {
      setSchedError(err?.error || t('organizer.errorSave'));
    } finally {
      setSchedLoading(false);
    }
  }

  const races = (champ.sessions || [])
    .filter(s => s.subsession_id)
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

  const scheduled = (champ.sessions || []).filter(s => !s.subsession_id);

  return (
    <div className="OrganizerPage">
      <Header />
      <main>
        <div className="organizer-content">

          <div className="organizer-panel-header">
            <div>
              <p className="organizer-panel-label">
                <Link to={`/leagues/${id}/organizer`} className="organizer-back-link">{t('organizer.backToPanel')}</Link>
              </p>
              <h1 className="organizer-panel-name">{champ.name}</h1>
              <p className="organizer-champ-meta" style={{ marginTop: 4 }}>
                {champ.active ? t('common.active') : t('common.finished')} · {(champ.sessions || []).length} {t('organizer.stages')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link
                to={`/leagues/${id}/organizer/championships/${champId}/points`}
                className="btn-secondary btn-sm"
              >
                {t('organizer.configureChamp')}
              </Link>
              <Link to={`/championships/${champ.championship_id}`} className="btn-secondary btn-sm">
                {t('organizer.viewPublic')}
              </Link>
            </div>
          </div>

          <div className="organizer-form-card" style={{ paddingTop: 16 }}>
            {/* Tab navigation */}
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 20 }}>
              {[
                { key: 'completed', label: t('organizer.completedRaces'), count: races.length },
                { key: 'scheduled', label: t('organizer.scheduledStages'), count: scheduled.length },
                { key: 'export', label: t('widget.exportTitle'), count: null },
              ].map(tab => (
                <button key={tab.key} type="button"
                  style={{
                    padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                    color: activeTab === tab.key ? 'var(--accent, #e8c84a)' : 'rgba(255,255,255,0.55)',
                    borderBottom: activeTab === tab.key ? '2px solid var(--accent, #e8c84a)' : '2px solid transparent',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    fontSize: '0.95rem', marginBottom: -1, transition: 'color 0.15s', display: 'flex', gap: 6, alignItems: 'center',
                  }}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.count !== null && (
                    <span style={{
                      fontSize: '0.72rem', padding: '1px 7px', borderRadius: 10,
                      background: activeTab === tab.key ? 'var(--accent, #e8c84a)' : 'rgba(255,255,255,0.12)',
                      color: activeTab === tab.key ? '#111' : 'rgba(255,255,255,0.6)',
                    }}>{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab: Corridas finalizadas */}
            {activeTab === 'completed' && (
              <>
              {isManual && (
                <form onSubmit={handleImportRace} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 20 }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      className="form-input"
                      placeholder={t('organizer.importSubsessionPlaceholder')}
                      value={importInput}
                      onChange={e => setImportInput(e.target.value)}
                      style={{ width: '100%', fontSize: '0.9rem' }}
                    />
                    {importError && <p style={{ color: '#e07070', fontSize: '0.78rem', marginTop: 4 }}>{importError}</p>}
                    {importSuccess && <p style={{ color: '#4caf50', fontSize: '0.78rem', marginTop: 4 }}>✓ {importSuccess}</p>}
                  </div>
                  <button
                    type="submit"
                    className="btn-primary btn-sm"
                    disabled={importLoading || !importInput.trim()}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {importLoading ? t('common.loading') : t('organizer.importRaceBtn')}
                  </button>
                </form>
              )}
              {races.length === 0 ? (
                <p className="organizer-empty">{t('organizer.noRacesImported')}</p>
              ) : (
                <div className="organizer-champ-list">
                  {races.map(session => (
                    <div key={session.subsession_id} className="organizer-champ-card">
                      <div className="organizer-champ-info">
                        <span className={`organizer-champ-badge ${session.result_status === 'confirmed' ? 'badge-confirmed' : 'badge-provisional'}`}>
                          {session.result_status === 'confirmed' ? t('organizer.confirmed') : t('races.provisional')}
                        </span>
                        <p className="organizer-champ-name">
                          {session.track_name}{session.config_name ? ` — ${session.config_name}` : ''}
                        </p>
                        <p className="organizer-champ-meta">{formatDate(session.start_time)}</p>
                      </div>
                      <div className="organizer-champ-actions">
                        <Link
                          to={`/races/${session.subsession_id}`}
                          className="btn-secondary btn-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('organizer.viewResult')}
                        </Link>
                        <Link
                          to={`/leagues/${id}/organizer/championships/${champId}/races/${session.subsession_id}`}
                          className="btn-primary btn-sm"
                        >
                          {t('organizer.postRace')}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>
            )}

            {/* Tab: Próximas corridas */}
            {activeTab === 'scheduled' && (
              <>
              {isManual && (
                <form onSubmit={handleAddScheduled} style={{ marginBottom: 20, padding: '14px 16px', background: '#161b22', borderRadius: 8, border: '1px solid #30363d' }}>
                  <div style={{ fontSize: '0.78rem', color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                    {t('organizer.addScheduledRace')}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 2, minWidth: 160 }}>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: 4 }}>{t('organizer.schedTrackName')} *</div>
                      <input
                        type="text"
                        className="form-input"
                        style={{ fontSize: '0.88rem' }}
                        placeholder={t('organizer.schedTrackPlaceholder')}
                        value={schedTrack}
                        onChange={e => setSchedTrack(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: 4 }}>{t('organizer.schedDate')}</div>
                      <input
                        type="datetime-local"
                        className="form-input"
                        style={{ fontSize: '0.88rem' }}
                        value={schedDate}
                        onChange={e => setSchedDate(e.target.value)}
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn-primary btn-sm"
                      disabled={schedLoading || !schedTrack.trim()}
                      style={{ flexShrink: 0 }}
                    >
                      {schedLoading ? t('common.loading') : t('organizer.schedAddBtn')}
                    </button>
                  </div>
                  {schedError && <p style={{ color: '#e07070', fontSize: '0.78rem', marginTop: 8 }}>{schedError}</p>}
                  {schedSuccess && <p style={{ color: '#4caf50', fontSize: '0.78rem', marginTop: 8 }}>✓ {schedSuccess}</p>}
                </form>
              )}
              {scheduled.length === 0 ? (
                <p className="organizer-empty">{t('organizer.noScheduledStages')}</p>
              ) : (
                <div className="organizer-champ-list">
                  {scheduled.map(session => (
                    <div key={session.league_session_id} className="organizer-champ-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="organizer-champ-info">
                          <p className="organizer-champ-name">
                            {session.track_name || t('organizer.trackNotDefined')}{session.config_name ? ` — ${session.config_name}` : ''}
                          </p>
                          <p className="organizer-champ-meta">{formatDate(session.start_time)}</p>
                          {preRaceUrls[session.league_session_id] && (
                            <p className="organizer-champ-meta" style={{ color: '#4caf50', marginTop: 2 }}>
                              {t('organizer.broadcastConfigured')}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span className="organizer-champ-badge badge-finished">{t('organizer.notCompleted')}</span>
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => {
                              setPreRaceSession(session.league_session_id);
                              setPreRaceUrl(preRaceUrls[session.league_session_id] || '');
                              setPreRaceItelemetry(session.itelemetry_url || '');
                            }}
                          >
                            {t('organizer.preRace')}
                          </button>
                        </div>
                      </div>

                      {isManual && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0 0', borderTop: '1px solid #21262d' }}>
                          <input
                            type="number"
                            className="form-input"
                            style={{ flex: 1, fontSize: '0.85rem' }}
                            placeholder={t('organizer.importSubsessionPlaceholder')}
                            value={completeInputs[session.league_session_id] || ''}
                            onChange={e => setCompleteInputs(s => ({ ...s, [session.league_session_id]: e.target.value }))}
                          />
                          <button
                            className="btn-primary btn-sm"
                            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            disabled={completeLoading[session.league_session_id] || !(completeInputs[session.league_session_id] || '').trim()}
                            onClick={() => handleCompleteRace(session.league_session_id)}
                          >
                            {completeLoading[session.league_session_id] ? t('common.loading') : t('organizer.associateResult')}
                          </button>
                        </div>
                      )}
                      {completeError[session.league_session_id] && (
                        <p style={{ color: '#e07070', fontSize: '0.78rem', margin: '4px 0 0' }}>{completeError[session.league_session_id]}</p>
                      )}

                      {preRaceSession === session.league_session_id && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 0' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              type="url"
                              className="form-input"
                              placeholder={t('organizer.broadcastPlaceholder')}
                              value={preRaceUrl}
                              onChange={e => setPreRaceUrl(e.target.value)}
                              style={{ flex: 1, fontSize: '13px' }}
                              autoFocus
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              type="url"
                              className="form-input"
                              placeholder={t('organizer.itelemetryPlaceholder')}
                              value={preRaceItelemetry}
                              onChange={e => setPreRaceItelemetry(e.target.value)}
                              style={{ flex: 1, fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="btn-primary btn-sm"
                              disabled={preRaceSaving}
                              onClick={() => handleSavePreRace(session.league_session_id)}
                            >
                              {preRaceSaving ? t('common.saving') : t('organizer.save')}
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              onClick={() => setPreRaceSession(null)}
                            >
                              {t('organizer.cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </>
            )}

            {/* Tab: Exportar campeonato */}
            {activeTab === 'export' && (
              subscription === null ? (
                <div style={{ padding: '16px 0' }}>
                  <p style={{ color: '#9ba3ab', fontSize: '0.88rem', marginBottom: 12 }}>{t('widget.noSubChamp')}</p>
                  <Link to={`/leagues/${id}/organizer`} className="btn-primary btn-sm">{t('widget.goToPanel')}</Link>
                </div>
              ) : subscription ? (
                <div style={{ padding: '8px 0' }}>
                  <p style={{ fontSize: '0.82rem', color: '#9ba3ab', marginBottom: 16 }}>{t('widget.exportDesc')}</p>
                  {(() => {
                    const base = window.location.origin;
                    const token = subscription.widget_token;
                    const frame = (id, path, height) => `<iframe id="${id}" src="${base}${path}?token=${token}" width="100%" height="${height}" scrolling="no" frameborder="0" style="border:none;"></iframe><script>(function(){var f=document.getElementById('${id}');window.addEventListener('message',function(e){if(e.source===f.contentWindow&&e.data&&e.data.type==='celeritas-widget-resize'){f.style.height=e.data.height+'px';}});})();</script>`;
                    const snippets = [
                       { key: 'standings', label: t('widget.iframeStandings'), value: frame('celeritas-standings', `/widget/leagues/${id}/championships/${champId}/standings`, 620) },
                       { key: 'schedule', label: t('widget.iframeSchedule'), value: frame('celeritas-schedule', `/widget/leagues/${id}/championships/${champId}/schedule`, 520) },
                       { key: 'results', label: t('widget.iframeResults'), value: frame('celeritas-results', `/widget/leagues/${id}/championships/${champId}/results`, 900) },
                      { key: 'api', label: t('widget.jsonApi'), value: `${window.location.origin.replace(':3000', ':8000')}/api/widget/${token}/championships/${champId}/` },
                    ];
                    return snippets.map(s => (
                      <div key={s.key} style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: '0.75rem', color: '#6e7681', marginBottom: 4 }}>{s.label}</div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                          <code style={{ flex: 1, background: '#161b22', borderRadius: 4, padding: '6px 10px', fontSize: '0.72rem', color: '#c0c8d0', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                            {s.value}
                          </code>
                          <button className="btn-secondary btn-sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => handleCopy(s.key, s.value)}>
                            {copyStates[s.key] ? t('widget.copied') : t('widget.copyBtn')}
                          </button>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <p style={{ color: '#9ba3ab', fontSize: '0.85rem' }}>{t('widget.loading')}</p>
              )
            )}
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}

export default OrganizerChampionshipDetail;
